<template>
  <div id="canvasAlarm" class="canvas-container" :class="{ warning: panelWarningActive }" @mousemove="handleCanvasPointerMove" @mouseleave="handleCanvasMouseLeave" @click="handleCanvasClick">
    <InfoPanel :info="info" :fateData="fateData" />
    <ConfigPanel
      :selected-planets="selectedPlanets"
      :selected-stars="selectedStars"
      :selected-points="selectedPoints"
      :selected-midpoints="selectedMidpointPairs"
      :permissions="permissions"
      @show-planet-config="handleOpenPlanetConfig"
      @show-star-config="handleOpenStarConfig"
      @show-point-config="handleOpenPointConfig"
      @show-midpoint-config="showMidpointConfig = true"
    />

    <PlanetConfigDialog
      :visible="showPlanetConfig"
      @update:visible="showPlanetConfig = $event"
      :planets="allPlanets"
      :toleranceMatrix="toleranceMatrix"
      :phaseToleranceRows="phaseToleranceRows"
      :defaultMatrix="defaultMatrix"
      @save="savePlanetConfig"
      @update-canvas-visible="applyCanvasVisibility"
      @update:matrix="$emit('update-tolerance-matrix', $event)"
      @update:phase-tolerance-rows="$emit('update-phase-tolerance-rows', $event)"
      @update-phases="$emit('update-phases', $event)"
      @click.native="handleConfigButtonClick"
    />

    <StarConfigDialog
      :visible="showStarConfig"
      @update:visible="showStarConfig = $event"
      :stars="allStars"
      @save="saveStarConfig"
      @update-canvas-visible="applyCanvasVisibility"
      @update:matrix="$emit('update-star-tolerance-matrix', $event)"
      @click.native="handleConfigButtonClick"
    />

    <LogicPointConfigDialog
      :visible="showPointConfig"
      @update:visible="showPointConfig = $event"
      :logicPoints="logicPointsForConfig"
      @save="saveLogicPointConfig"
      @update-canvas-visible="applyCanvasVisibility"
      @click.native="handleConfigButtonClick"
    />

    <MidpointConfigDialog
      :visible="showMidpointConfig"
      @update:visible="showMidpointConfig = $event"
      :planets="allPlanets"
      :selected-midpoints="selectedMidpointPairs"
      @save="saveMidpointConfig"
      @click.native="handleConfigButtonClick"
    />

    <!-- A 方案要求静默加载，这里保留 loading 状态但不显示文字 -->

    <Tooltip
      ref="tooltipElement"
      :visible="tooltip.visible"
      :position="tooltip.position"
      :movement="tooltip.movement"
      :items="tooltip.items"
      @mouseenter="handleTooltipEnter"
      @mouseleave="handleTooltipLeave"
      @save-remark="handleSaveRemark"
      @editing-change="handleEditingChange"
      @click.native.stop
      @mousedown.native.stop
    />

    <DraggableScoreTable
      :visible="scoreTableVisible && permissions.includes('planet_power')"
      :position="scoreTablePosition"
      :tableData="scoreTableData"
      :width="120"
      @drag-start="handleScoreTableDragStart"
      @drag-move="handleScoreTableDragMove"
      @drag-end="handleScoreTableDragEnd"
    />
    <canvas ref="canvas" :width="canvasSize+80" :height="canvasSize"></canvas>
  </div>
</template>

<script>
import CanvasUtils from '@/utils/CanvasUtils'
import AstrolabeRenderer from '@/utils/AstrolabeRenderer'
import Tooltip from '@/components/astrolabe/Tooltip'
import PermissionService from '@/services/PermissionService'
import InfoPanel from '@/components/astrolabe/InfoPanel'
import ConfigPanel from '@/components/astrolabe/ConfigPanel'
import PlanetConfigDialog from '@/components/astrolabe/PlanetConfigDialog'
import StarConfigDialog from '@/components/astrolabe/StarConfigDialog'
import LogicPointConfigDialog from '@/components/astrolabe/LogicPointConfigDialog'
import MidpointConfigDialog from '@/components/astrolabe/MidpointConfigDialog'
import AstrolabeDrawer from '@/utils/AstrolabeDrawer'

// 新增导入
import DraggableScoreTable from '@/components/astrolabe/DraggableScoreTable'

const { deriveAstrolabeAlarmState } = require('../../utils/astrolabeInteractionGuards')
// 恒星管控：受管控恒星恒为已选、默认不上画布
const starControl = require('../../features/tianxing/utils/starControl')
const { getAlarmEntitlement, readStoredTianxingUser } = require('../../services/userContext')
// 配置弹窗入口判权（2026-10-11）：恒星/行星/逻辑点三项「管控」权益共用同一份目录与判定
const purchaseCatalog = require('../../features/tianxing/utils/permissionPurchaseCatalog')
const { isPointerInTooltipTransferZone } = require('../../utils/tooltipGuards')
const { buildTooltipRemarkStorageKey } = require('../../utils/tooltipRemarkKeys')
const {
  normalizeAngle,
  resolveChartAscAbsoluteLongitude,
  resolveProjectedZuoshan,
  buildChartDrawData
} = require('../../utils/astrolabeOverlayGeometry')

const LEGACY_NEBULA_LOGIC_POINT_ID = 46
const FRONTEND_DUMMY_YOUYI_POINT_ID = 9000046
const FRONTEND_DUMMY_YOUYI_POINT_NAME = '游弋点'

// 2026-10-03 总经理要求：地平盘隐藏 ASC(红)/MC(蓝) 两根轴线。
// 需要恢复时把开关改回 true（AstrolabeDrawer.drawHorizonSpecialAxes 与其用例保留未删）。
const SHOW_HORIZON_SPECIAL_AXES = false
// 2026-10-03 总经理要求：坐山指针（绿色贯穿箭头）只在地平盘显示，其余盘面一律隐藏。
// 需要恢复旧行为（非地平盘购买 zuoshan_pointer 权限后也绘制）时改回 true。
const SHOW_ZUOSHAN_POINTER_ON_OTHER_CHARTS = false
// 2026-10-04 总经理要求：地平盘隐藏 ASC/MC 轴线后，与它们相关的相位信息（连线 + 悬浮）也不应继续显示。
// 数组内容 = [MC, ASC]：MC = 1000014、ASC = 1000015（名「命」），是后端注入的虚拟轴点，
// 在相位里以 planet1Index / planet2Index 出现。
const AXIS_VIRTUAL_POINT_IDS = [1000014, 1000015]

export default {
  name: 'AstrolabeCanvas',
  components: {
    Tooltip,
    InfoPanel,
    ConfigPanel,
    PlanetConfigDialog,
    StarConfigDialog,
    LogicPointConfigDialog,
    MidpointConfigDialog,
    DraggableScoreTable // 新增组件
    // AstrolabeDrawer
  },
  props: {
    astrolabeData: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    renderRevision: {
      type: Number,
      default: 0
    },
    // 新增planetList属性接收行星列表
    planetList: {
      type: Array,
      default: () => []
    },
    starList: {
      type: Array,
      default: () => []
    },
    // 逐颗恒星容许度（starNum -> 度），来自用户配置：
    // 只用于「弹窗回显」——取值落到恒星对象的 tolerance 上，供 StarConfigDialog 显示已保存值。
    // 未配置的恒星不带该字段，弹窗回落默认 1°0'0"（与历史行为一致）。
    starToleranceMap: {
      type: Object,
      default: () => ({})
    },
    // 新增logicPointList属性接收逻辑点列表
    logicPointList: {
      type: Array,
      default: () => []
    },
    // 新增info属性接收表单数据
    info: {
      type: Object,
      default: () => ({
        name: '',
        address: '',
        solarDate: '',
        solarTime: '',
        lunarDate: '',
        lunarTime: '',
        longitude: '',
        latitude: '',
        hsys: '',
        astrolabeType: ''
      })
    },
    // 新增：分数表格数据
    scoreTableData: {
      type: Array,
      default: () => [
        { star: '太阳', score: 95 },
        { star: '月亮', score: 88 },
        { star: '水星', score: 76 },
        { star: '金星', score: 92 },
        { star: '火星', score: 81 }
      ]
    },
    // 本命盘数据（用于黄道盘外圈绘制）
    natalAstrolabeData: {
      type: Object,
      default: () => ({})
    },
    // 新增：分数表格显示状态
    scoreTableVisible: {
      type: Boolean,
      default: true
    },
    permissions: {
      type: Array,
      default: () => []
    },
    // 新增：刻度显示控制开关
    showScaleCircle: {
      type: Boolean,
      default: false
    },
    showFixRing: {
      type: Boolean,
      default: false
    },
    // 新增：命理数据
    fateData: {
      type: Object,
      default: () => ({})
    },
    // 新增：行星相位容许误差矩阵
    toleranceMatrix: {
      type: Array,
      default: () => []
    },
    // 新增：显式相位容许度协议行
    phaseToleranceRows: {
      type: Array,
      default: () => []
    },
    // 新增：行星相位容许误差默认矩阵
    defaultMatrix: {
      type: Array,
      default: () => []
    },
    // 新增：坐山数据
    zuoshan: {
      type: Number,
      default: 0
    },
    // 新增：坐山箭头（绿色贯穿箭头）显示开关，默认关闭：
    // 该箭头从坐山一侧直达对宫，会横跨宫位圈/星宿圈等多个圈层，影响盘面观感。
    // 2026-10-11：宿主 AstrolabeView 已按「仅地平盘」传入（`astrolabeType === 'horizon'`），
    // 其余盘面仍为关闭；本组件的绘制分支另有 SHOW_ZUOSHAN_POINTER_ON_OTHER_CHARTS 兜底。
    showZuoshanArrow: {
      type: Boolean,
      default: false
    },
    // 新增：选中的相位列表（用于过滤相位线显示）
    selectedPhases: {
      type: Array,
      default: () => []
    },
    // 新增：单元格选择状态（用于更精细的相位过滤）
    phaseCellSelections: {
      type: Array,
      default: () => []
    },
    // 新增：等分宫位开关（true=使用等分宫位绘制，false=使用标准绘制）
    ehFlag: {
      type: Boolean,
      default: false
    },
    selectedPlanetIds: {
      type: Array,
      default: () => []
    },
    selectedStarIds: {
      type: Array,
      default: () => []
    },
    selectedPointIds: {
      type: Array,
      default: () => []
    },
    selectedMidpointPairs: {
      type: Array,
      default: () => []
    },
    tooltipRemarkMap: {
      type: Object,
      default: () => ({})
    },
    tooltipInteractionMode: {
      type: String,
      default: 'click'
    }
  },
  data () {
    return {
      canvasSize: 800,
      // 新增：刻度样式配置
      scaleStyles: {
        show: false, // 默认不显示
        outerRadiusRatio: 0.9, // 刻度圈外半径比例（在星盘外）
        largeTickLength: 15, // 大刻度线长度（像素）
        smallTickLength: 8, // 小刻度线长度（像素）
        tickColor: '#666', // 刻度线颜色
        textColor: '#333', // 文字颜色
        textFont: '12px Arial', // 文字字体
        textOffset: 25 // 文字距离圆心的偏移量
      },
      // 样式配置
      styles: {
        phaseSymbol: {
          font: '16px Arial',
          offset: 0, // 符号偏移距离
          gapSize: 16, // 符号周围不画线的间隙大小
          colors: [
            '#FF0000', // 0度
            '#FF9900', // 30度
            '#00FF00', // 45度
            '#0000FF', // 60度
            '#FF00FF', // 72度
            '#00FFFF', // 90度
            '#D97706', // 120度
            '#FF6600', // 135度
            '#6600FF', // 144度
            '#0066FF', // 150度
            '#9900FF' // 180度
          ]
        }
      },
      renderer: null, // 渲染器实例
      astrolabeDrawer: null, // 星盘绘制器实例
      center: null, // 画布中心点
      radius: null, // 星盘半径
      zeroAngle: 180, // 9点钟方向为0度

      tooltip: {
        visible: false,
        position: { x: 0, y: 0 },
        movement: { x: 0, y: 0 },
        items: []
      },
      lastMouseClientPosition: null,
      lastPointerMovement: { x: 0, y: 0 },
      hoverItems: [], // 存储当前悬停位置的所有元素
      // 新增：存储定时器引用
      tooltipTimer: null,

      // 新增配置面板相关数据
      showPlanetConfig: false,
      showStarConfig: false,
      showPointConfig: false,
      showMidpointConfig: false,

      // 行星数据
      allPlanets: [],
      allStars: [],
      allLogicPoints: [],

      // 默认选中的行星ID
      // 日月水金火木土天海冥 + 计都 + MC + ASC + 福点
      defaultSelectedPlanetIds: [],
      defaultSelectedStarIds: [], // 默认选中的恒星ID
      defaultSelectedPointIds: [], // 默认选中的逻辑点ID
      // 福点（幸运点）必须始终显示的名称关键字
      fortunePointNames: ['福点', '幸运点', 'Fortune', 'Pars Fortuna'],

      // 新增：悬浮表格相关数据
      scoreTablePosition: {
        x: 20, // 默认位置
        y: 60
      },
      isDraggingScoreTable: false,
      // 拖动起始快照：记录按下瞬间的指针坐标与表格位置，位移按增量计算。
      // 之前用 dragOffset = clientX - position.x 把视口坐标与容器坐标混在一起计算，
      // 页面滚动（手机横滑必然发生）后再拖动会整体跳变。
      scoreTableDragStart: { pointerX: 0, pointerY: 0, posX: 0, posY: 0 },

      // 新增：初始化完成标志，防止初始加载时重复请求
      initialized: false,
      drawAnimationFrameData: null,
      lastCompletedRenderRevision: 0,
      panelWarningActive: false,
      isEditingRemark: false // 新增：是否正在编辑备注
    }
  },
  computed: {
    resolvedTooltipInteractionMode () {
      return this.normalizeTooltipInteractionMode(this.tooltipInteractionMode)
    },
    isHoverTooltipMode () {
      return this.resolvedTooltipInteractionMode === 'hover'
    },
    isClickTooltipMode () {
      return this.resolvedTooltipInteractionMode === 'click'
    },
    isAnyDialogOpen () {
      return this.showPlanetConfig || this.showStarConfig || this.showPointConfig || this.showMidpointConfig
    },
    selectedPlanets () {
      return this.allPlanets.filter(p => p.selected)
    },
    selectedStars () {
      return this.allStars.filter(s => s.selected)
    },
    // 过滤掉福点（id=1）的逻辑点列表，用于配置界面显示
    logicPointsForConfig () {
      return this.allLogicPoints.filter(p => p.id !== 1)
    },
    selectedPoints () {
      return this.allLogicPoints.filter(p => p.selected)
    }
  },
  watch: {
    astrolabeData: {
      deep: true,
      handler () {
        // 第一次接收到数据时，设置初始化完成标志
        if (this.astrolabeData && !this.initialized) {
          this.initialized = true
        }

        this.syncAlarmEntitlement()
      }
    },
    // 监听本命盘数据变化 - 无论显示还是隐藏，都要重绘一次，避免 canvas 残留旧的本命盘外圈
    natalAstrolabeData: {
      deep: true,
      handler () {
        if (this.renderer && this.astrolabeDrawer) {
          this.scheduleDraw()
        }
      }
    },
    renderRevision: {
      immediate: true,
      handler () {
        if (!this.renderer || !this.astrolabeDrawer) {
          return
        }
        if (this.hasChartData()) {
          this.scheduleDraw()
          return
        }
        // 数据为空（无权限 / 生成失败 / 数据被清空）时同步清空画布：
        // 既清掉上一次盘面的残留，也避免空数据绘制出白色底圆
        this.clearCanvas()
        this.emitRenderComplete()
      }
    },
    // 监听行星列表变化
    planetList: {
      immediate: true,
      handler (newList) {
        this.initializePlanets(newList, !this.initialized)
      }
    },
    starList: {
      immediate: true,
      handler (newList) {
        this.initializeStars(newList)
      }
    },
    // 恒星容许度配置变化（例如异步加载完用户配置后才回填）→ 重新落到恒星对象上，让弹窗回显已保存值。
    // 只改本组件内部的展示数据，不触发重新生成星盘。
    starToleranceMap: {
      deep: true,
      handler () {
        if (!Array.isArray(this.allStars) || this.allStars.length === 0) {
          return
        }
        this.allStars = this.allStars.map(star => this.applyStarTolerance(star))
      }
    },
    // 监听逻辑点列表变化
    logicPointList: {
      immediate: true,
      handler (newList) {
        this.initializeLogicPoints(newList, !this.initialized)
      }
    },
    // 新增：监听刻度显示开关
    showScaleCircle: {
      immediate: true,
      handler (newVal) {
        this.scaleStyles.show = newVal
        if (this.astrolabeDrawer) {
          this.astrolabeDrawer.updateScaleStyles(this.scaleStyles)
        }
      }
    },
    // 新增：监听等分宫位开关
    ehFlag: {
      immediate: true,
      handler (newVal) {
        if (this.astrolabeDrawer) {
          this.astrolabeDrawer.setEhFlag(newVal)
          // 重新绘制星盘
          this.scheduleDraw()
        }
      }
    },
    // 新增：监听坐山箭头开关（关闭后不再绘制绿色贯穿箭头）
    showZuoshanArrow: {
      handler () {
        if (this.astrolabeDrawer) {
          this.scheduleDraw()
        }
      }
    },
    selectedPlanetIds: {
      immediate: true,
      deep: true,
      handler (newValue) {
        this.defaultSelectedPlanetIds = Array.isArray(newValue) ? [...newValue] : []
        if (this.allPlanets.length > 0) {
          this.allPlanets = this.allPlanets.map(planet => ({
            ...planet,
            selected: this.defaultSelectedPlanetIds.includes(planet.id)
          }))
        }
      }
    },
    selectedStarIds: {
      immediate: true,
      deep: true,
      handler (newValue) {
        this.defaultSelectedStarIds = Array.isArray(newValue) ? [...newValue] : []
        if (this.allStars.length > 0) {
          this.allStars = this.allStars.map(star => ({
            ...star,
            // 受管控恒星不受已选列表变化影响，恒为已选
            selected: starControl.isControlledStar(star.id) || this.defaultSelectedStarIds.includes(star.id)
          }))
        }
      }
    },
    selectedPointIds: {
      immediate: true,
      deep: true,
      handler (newValue) {
        this.defaultSelectedPointIds = Array.isArray(newValue) ? [...newValue] : []
        if (this.allLogicPoints.length > 0) {
          this.allLogicPoints = this.allLogicPoints.map(point => ({
            ...point,
            selected: this.defaultSelectedPointIds.includes(point.id) || this.isFortunePoint(point.name)
          }))
        }
      }
    },
    tooltipInteractionMode () {
      this.hideTooltip(true)
    }
  },
  mounted () {
    this.initCanvas()
    // 新增：初始化刻度样式
    this.scaleStyles.show = this.showScaleCircle
    // 新增：计算悬浮框初始位置（canvas右边沿向左200px）
    this.$nextTick(() => {
      const canvasEl = this.$refs.canvas
      if (canvasEl) {
        const rect = canvasEl.getBoundingClientRect()
        this.scoreTablePosition = {
          x: rect.right - 80, // canvas右边沿向左60px
          y: 20
        }
      }
    })
  },
  beforeDestroy () {
    if (this.drawAnimationFrameData) {
      cancelAnimationFrame(this.drawAnimationFrameData)
      this.drawAnimationFrameData = null
    }
    if (this.astrolabeDrawer) {
      this.astrolabeDrawer.stopMsTimer()
    }
    this.closeWarning()
  },
  methods: {
    hasPermission (key) {
      return PermissionService.hasPermission(key)
    },
    /**
     * 配置弹窗入口判权（2026-10-11）。
     *
     * 恒星配置 / 行星配置 / 逻辑点 三项，分别需要 star_control（恒星管控）/
     * planet_control（行星管控）/ logic_point_control（逻辑点管控）权益，
     * 读取登录态 user 的到期字段（口径与 pointAlarm / areaAlarm 完全一致）。
     *
     * 未开通时**不打开配置弹窗**，而是把请求抛给宿主 AstrolabeView：
     * 由宿主提示「该功能需先开通…」并打开「开通功能」弹窗定位到该项，指引扫码付款；
     * 支付成功后宿主会刷新登录态，用户可再次点击进入配置。
     */
    canOpenConfigDialog (key) {
      if (purchaseCatalog.isPermissionActive(key, readStoredTianxingUser())) {
        return true
      }
      this.$emit('open-feature-purchase', key)
      return false
    },
    handleOpenStarConfig () {
      if (!this.canOpenConfigDialog('star_control')) return
      this.showStarConfig = true
    },
    handleOpenPlanetConfig () {
      if (!this.canOpenConfigDialog('planet_control')) return
      this.showPlanetConfig = true
    },
    handleOpenPointConfig () {
      if (!this.canOpenConfigDialog('logic_point_control')) return
      this.showPointConfig = true
    },
    normalizeTooltipInteractionMode (mode) {
      const normalizedMode = `${mode || ''}`.trim().toLowerCase()
      if (['hover', 'mouseover', '悬停'].includes(normalizedMode)) {
        return 'hover'
      }
      if (['click', '点击'].includes(normalizedMode)) {
        return 'click'
      }
      return 'click'
    },
    emitRenderComplete () {
      const revision = Number(this.renderRevision)
      if (!Number.isFinite(revision) || revision <= this.lastCompletedRenderRevision) {
        return
      }
      this.lastCompletedRenderRevision = revision
      this.$emit('render-complete', revision)
    },
    /**
     * 当前是否已有可绘制的星盘数据
     *
     * 数据为空时绝不能进入 drawAstrolabe：它会先 clearRect 再画一个白色底圆，
     * 而空数据下后续各图层全部画不出来，画布上就只剩一个白圆
     * （即线上反馈的「星盘画布未展示效果，出现白色圆形」）。
     */
    hasChartData () {
      return Boolean(this.astrolabeData && Object.keys(this.astrolabeData).length > 0)
    },

    /**
     * 清空画布（数据为空 / 绘制异常时调用，保持干净的空态而不是留下白圆）
     */
    clearCanvas () {
      if (!this.renderer || !this.$refs.canvas) {
        return
      }
      const canvasEl = this.$refs.canvas
      this.renderer.ctx.clearRect(0, 0, canvasEl.width, canvasEl.height)
    },

    scheduleDraw () {
      if (this.drawAnimationFrameData) {
        cancelAnimationFrame(this.drawAnimationFrameData)
      }
      this.drawAnimationFrameData = requestAnimationFrame(() => {
        this.drawAnimationFrameData = null
        if (!this.renderer || !this.astrolabeDrawer) {
          return
        }

        // 所有重绘入口（本命盘数据变化、坐山开关、等分宫位、配置弹窗确定等）
        // 最终都汇聚到这里，因此数据守卫统一加在这一层：
        // 没有数据时只清空画布，不再画出白色底圆。
        if (!this.hasChartData()) {
          this.clearCanvas()
          this.emitRenderComplete()
          return
        }

        try {
          this.drawAstrolabe()
        } catch (error) {
          // 绘制中途异常时清空画布，避免留下「白圆 + 半成品」的误导性画面
          console.error('[AstrolabeCanvas] 星盘绘制失败:', error)
          this.clearCanvas()
        }
        this.emitRenderComplete()
      })
    },

    /**
     * 2026-10-04 总经理要求（原文）：
     *   「既然地平盘隐藏了ASC和MC，那么与他们相关的相位信息就不应该继续显示了，
     *     你可以选择隐藏、也可以选择不请求计算。你来决策。」
     *
     * 决策：采用「前端按盘型过滤（渲染层隐藏）」，不改请求参数。理由：
     *   1) selectedPlanets 是全局持久化配置，黄道盘/地平黄道盘的轴线与相位都依赖
     *      1000014(MC)/1000015(ASC)，按盘型裁剪请求会造成「同一份配置、不同盘型发不同请求」的隐性差异；
     *   2) ASC/MC 的方位角数据后端本来就要算（horizontalSpecialPoints 与 selectedPlanets 无关），
     *      裁剪请求省不掉多少计算；
     *   3) 渲染层过滤一处即可同时消掉「相位连线 + 悬浮提示」（两者同源 aspectPositions），风险最低；
     *   4) 数据仍保留在响应里，将来恢复地平盘轴线（SHOW_HORIZON_SPECIAL_AXES 改回 true）时，相关相位随之自动恢复。
     *
     * 生效范围：仅「地平盘」且「轴线开关关闭」时过滤；其他盘型（黄道盘/地平黄道盘/赤道盘）与正常模式原样返回。
     * 边界：本轮只处理相位；相位容许度设置弹窗（全局配置，含 MC/ASC 行）与中点配置不动。
     */
    excludeAxisRelatedPhases (phases = []) {
      if (SHOW_HORIZON_SPECIAL_AXES) return phases
      if (this.info.astrolabeType !== '地平盘') return phases
      return phases.filter(phase => {
        if (!phase) return false
        return AXIS_VIRTUAL_POINT_IDS.indexOf(phase.planet1Index) === -1 &&
          AXIS_VIRTUAL_POINT_IDS.indexOf(phase.planet2Index) === -1
      })
    },

    // 过滤相位数据，只保留选中的相位
    filterPhases (phases) {
      if (!phases || phases.length === 0) return []
      if (!this.selectedPhases || this.selectedPhases.length === 0) return []

      // 地平盘隐藏 ASC/MC 轴线后，与这两个轴点相关的相位一并不显示（口径与决策见 excludeAxisRelatedPhases）
      const sourcePhases = this.excludeAxisRelatedPhases(phases)

      // 获取选中的相位索引集合
      const selectedRuleIndices = this.selectedPhases
        .filter(p => p.selected)
        .map(p => p.ruleIndex)

      // 如果有单元格选择状态，使用更精细的过滤
      if (this.phaseCellSelections && this.phaseCellSelections.length > 0) {
        // 行星ID到toleranceRows索引的映射
        const planetIdToRowIndex = {
          0: 0, // 日
          1: 1, // 月
          2: 2, // 水
          3: 3, // 金
          4: 4, // 火
          5: 5, // 木
          6: 6, // 土
          7: 7, // 天
          8: 8, // 海
          9: 9, // 冥
          11: 10, // 计都
          13: 11, // 月孛
          15: 12, // 凯龙
          1000014: 13, // MC
          1000015: 14, // ASC
          1000016: 15, // 福点
          1000012: 16, // 紫气
          1000013: 17 // 罗睺
        }

        // 列索引到ruleIndex的映射（列顺序：合相,二分相,四分相,三分相,六分相,30°,45°,135°,150°,72°,144°）
        const colIndexToRuleIndex = {
          0: 0, // 合相
          1: 10, // 二分相
          2: 5, // 四分相
          3: 6, // 三分相
          4: 3, // 六分相
          5: 1, // 30°
          6: 2, // 45°
          7: 7, // 135°
          8: 9, // 150°
          9: 4, // 72°
          10: 8 // 144°
        }

        return sourcePhases.filter(phase => {
          const ruleIndex = phase.ruleIndex
          if (ruleIndex === undefined || !selectedRuleIndices.includes(ruleIndex)) {
            return false
          }

          // 找到对应的列索引
          let colIndex = -1
          for (const [ci, ri] of Object.entries(colIndexToRuleIndex)) {
            if (ri === ruleIndex) {
              colIndex = parseInt(ci)
              break
            }
          }
          if (colIndex === -1) return false

          // 检查两个行星是否都在容许度表格中被点亮
          const row1Index = planetIdToRowIndex[phase.planet1Index]
          const row2Index = planetIdToRowIndex[phase.planet2Index]

          // 如果任一行不在映射中，或者对应的单元格未被点亮，则不显示
          if (row1Index === undefined || row2Index === undefined) {
            // 对于未知行星（如某些特殊点），使用ruleIndex过滤即可
            return true
          }

          const row1Selected = this.isCellSelected(row1Index, colIndex)
          const row2Selected = this.isCellSelected(row2Index, colIndex)

          // 只有当两个行星对应的单元格都被点亮时才显示
          return row1Selected && row2Selected
        })
      }

      // 没有单元格选择状态时，只根据ruleIndex过滤
      return sourcePhases.filter(phase => {
        const ruleIndex = phase.ruleIndex
        return ruleIndex !== undefined && selectedRuleIndices.includes(ruleIndex)
      })
    },
    // 判断单元格是否被选中
    isCellSelected (rowIndex, colIndex) {
      if (!this.phaseCellSelections || !this.phaseCellSelections[rowIndex]) {
        return true // 默认选中
      }
      const cellState = this.phaseCellSelections[rowIndex][colIndex]
      // null/undefined 表示跟随相位选择状态，视为选中
      if (cellState === null || cellState === undefined) {
        return true
      }
      return cellState === true
    },
    /**
     * 按当前登录态同步画布报警效果（点报警 pointAlarm / 面报警 areaAlarm）。
     *
     * 权益取自天星网站全局登录态（localStorage.user）里的到期时间：没有该字段、
     * 或当前时间已过到期时间 = 无权限，不展示对应的报警效果。
     *
     * 抽成方法的原因：报警权益可能在盘面已渲染之后才开通（星盘页「开通功能」里
     * 0 元的点/面报警点一下就开通一年），此时需要就地重新判定一次，而不必等下一次换盘。
     * 数据为空（无权限 / 生成失败）时不做任何事，与原 watcher 行为一致。
     */
    syncAlarmEntitlement () {
      if (!this.hasChartData() || !this.renderer || !this.astrolabeDrawer) {
        return
      }

      const { hasBlinkAlarm, hasPanelAlarm } = deriveAstrolabeAlarmState(this.astrolabeData.starPositions)
      const alarmEntitlement = getAlarmEntitlement()

      // 点报警（pointAlarm）：恒星点闪烁
      if (hasBlinkAlarm && alarmEntitlement.pointAlarm) {
        this.astrolabeDrawer.startMsTimer(500, () => {
          const referenceAsc = resolveChartAscAbsoluteLongitude(this.astrolabeData)
          const preparedAstrolabeData = buildChartDrawData(this.astrolabeData, {
            referenceAscAbsoluteLongitude: referenceAsc,
            equalHouse: this.ehFlag
          })
          // 报警闪烁重绘同样遵守「画布展示确认框」：未勾选的恒星不绘制
          this.astrolabeDrawer.drawStars(this.filterCanvasVisibleStars(preparedAstrolabeData.starPositions || []))
        })
      } else {
        this.astrolabeDrawer.stopMsTimer()
      }

      // 面报警（areaAlarm）：画布整块红光闪烁
      if (hasPanelAlarm && alarmEntitlement.areaAlarm) {
        this.blinkWarning()
      } else {
        this.closeWarning()
      }

      this.scheduleDraw()
    },
    // 闪烁开
    blinkWarning () {
      this.panelWarningActive = true
    },
    // 闪烁关
    closeWarning () {
      this.panelWarningActive = false
    },

    normalizeDmsPart (value) {
      if (value === undefined || value === null) {
        return ''
      }
      return `${value}`
        .replace(/[°º度′'分″"秒]/g, '')
        .trim()
    },

    formatDms (deg, min, sec) {
      const resolvedDeg = this.normalizeDmsPart(deg)
      const resolvedMin = this.normalizeDmsPart(min)
      const resolvedSec = this.normalizeDmsPart(sec)
      if (!resolvedDeg && !resolvedMin && !resolvedSec) {
        return ''
      }
      return `${resolvedDeg}°${resolvedMin}′${resolvedSec}″`
    },

    formatSignPosition (signName, deg, min, sec) {
      const formattedDms = this.formatDms(deg, min, sec)
      const prefix = signName ? `${signName} ` : ''
      if (!formattedDms) {
        return signName ? ` ${signName}` : ''
      }
      return ` ${prefix}${formattedDms}`
    },

    /**
     * 将角度值格式化为「度分秒」字符串（分、秒补零到两位）。
     * @param {number} value - 角度值
     * @param {boolean} withSign - 是否强制带正负号（纬度需要）
     * @returns {string} 如 178°16′10″ / +0°00′01″
     */
    formatCoordinateDms (value, withSign = false) {
      const numeric = Number(value)
      if (!Number.isFinite(numeric)) {
        return ''
      }
      const { deg, min, sec } = CanvasUtils.convertAngleToDMS(Math.abs(numeric))
      const pad = (part) => String(part).padStart(2, '0')
      const sign = withSign ? (numeric < 0 ? '-' : '+') : ''
      return `${sign}${deg}°${pad(min)}′${pad(sec)}″`
    },

    /**
     * 按候选字段路径取一个数值（支持 "geometry.absoluteLongitude" 这种路径写法）。
     * @param {Object} data - 数据对象
     * @param {Array<string>} paths - 候选字段路径，按优先级排列
     * @returns {number|null} 取不到时返回 null
     */
    resolveNumericByPaths (data, paths) {
      if (!data) {
        return null
      }
      for (const path of paths) {
        const raw = path.split('.').reduce(
          (acc, part) => (acc === undefined || acc === null ? undefined : acc[part]),
          data
        )
        if (raw === undefined || raw === null || raw === '') {
          continue
        }
        const numeric = Number(raw)
        if (Number.isFinite(numeric)) {
          return numeric
        }
      }
      return null
    },

    /**
     * 拼装悬浮框里的黄纬文案。
     *
     * 这里**只输出黄纬，不再输出黄经**：黄经就是盘面上已经画出来的位置（行星/恒星的点位、
     * 先天宫与后天宫的扇区边界、ASC/MC 轴线都按黄经落点），球面文案里再写一遍属于重复显示；
     * 黄纬不会画在盘面上，所以保留。无论黄道盘、地平盘还是固定环模式，口径一致。
     *
     * 各类数据字段不统一：行星是 eclipticLatitudeJ2000，恒星/星宿/宫位等当前大多没有黄纬字段
     *（若后端后续补上，字段名预计是 starRLat / xingxiuRLat，这里已兼容），取不到就整段不显示，
     * 避免出现 undefined。
     *
     * @param {Object|null} data - 悬浮命中项的数据对象
     * @returns {string} 形如 " 黄纬:+0°00′01″"，无可用数据时返回空串
     */
    formatEclipticLatitudeDetail (data) {
      const latitude = this.resolveNumericByPaths(data, [
        'eclipticLatitudeJ2000',
        'eclipticLatitude',
        'absoluteLatitude',
        'geometry.absoluteLatitude',
        // 恒星/星宿若后续由后端补上黄纬，字段名预计是 starRLat / xingxiuRLat，这里先兼容
        'starRLat',
        'xingxiuRLat'
      ])
      if (latitude === null) {
        return ''
      }
      return ` 黄纬:${this.formatCoordinateDms(latitude, true)}`
    },

    sortPlanetsForDisplay (planets) {
      const sortedPlanets = [...planets]
      const moveBefore = (sourceId, targetId) => {
        const sourceIndex = sortedPlanets.findIndex(planet => planet.id === sourceId)
        const targetIndex = sortedPlanets.findIndex(planet => planet.id === targetId)
        if (sourceIndex === -1 || targetIndex === -1 || sourceIndex < targetIndex) {
          return
        }
        const [planet] = sortedPlanets.splice(sourceIndex, 1)
        const nextTargetIndex = sortedPlanets.findIndex(item => item.id === targetId)
        sortedPlanets.splice(nextTargetIndex, 0, planet)
      }

      moveBefore(1000012, 13)
      moveBefore(1000013, 11)
      return sortedPlanets
    },

    // 初始化行星数据
    initializePlanets (planetList, skipGenerate = false) {
      const HIDDEN_IDS = new Set([1000017, 1000018, 1000019])
      const selectedIds = this.resolveEffectiveSelectedIds(this.selectedPlanetIds, this.defaultSelectedPlanetIds)
      this.allPlanets = this.sortPlanetsForDisplay(planetList
        .filter(planet => !HIDDEN_IDS.has(planet.id))
        .map(planet => {
          const selected = selectedIds.includes(planet.id)
          return {
            id: planet.id,
            name: planet.name,
            selected,
            // 画布展示确认框：渲染按钮时跟随该分类的已选状态（已选=勾选），
            // 用户可再手动取消勾选。勾选状态只影响画布绘制，
            // 选中状态与其它逻辑（相位、容许度、请求参数等）均不受影响。
            visibleOnCanvas: selected
          }
        }))
      // 初始化完成后，自动更新选中的行星到父组件（如果不是在初始化阶段）
      if (!skipGenerate && this.initialized) {
        this.$nextTick(() => {
          this.savePlanetConfig(true)
        })
      }
    },
    // 初始化恒星数据
    initializeStars (starList) {
      const selectedIds = this.resolveEffectiveSelectedIds(this.selectedStarIds, this.defaultSelectedStarIds)
      this.allStars = (Array.isArray(starList) ? starList : []).map(star => this.applyStarTolerance({
        id: star.id,
        name: star.name,
        // 受管控恒星恒为已选：父组件的 selectedStars 已做并集，这里再兜一层，
        // 避免「星表先到、已选列表后到」的时序把受管控恒星渲染成未选中。
        selected: starControl.isControlledStar(star.id) || selectedIds.includes(star.id),
        // 画布展示确认框：受管控恒星默认不上画布（但照常参与计算与点/面报警判定）。
        // 报警闪烁重绘同样经过 filterCanvasVisibleStars，因此不会被暴露出来。
        visibleOnCanvas: !starControl.isControlledStar(star.id) && selectedIds.includes(star.id)
      }))
    },

    /**
     * 把用户配置里的容许度落到单颗恒星对象上（**弹窗回显用**）。
     *
     * 只影响 StarConfigDialog 里显示的度/分/秒（`floatToDMS` 读 `star.tolerance`）：
     * 配置里没有该恒星时不写 tolerance，弹窗按默认 1°0'0" 显示，与历史行为一致。
     * 注意这里只做展示回显，不参与画布绘制；真正影响恒星报警判定的是随星盘请求
     * 一起下发的 `starToleranceMap`。
     */
    applyStarTolerance (item) {
      const saved = this.starToleranceMap ? this.starToleranceMap[String(item.id)] : undefined
      const tolerance = Number(saved)
      const next = { ...item }
      if (saved === undefined || saved === null || !Number.isFinite(tolerance) || tolerance <= 0) {
        delete next.tolerance
        return next
      }
      next.tolerance = tolerance
      return next
    },

    // 取「当前生效的已选 id 列表」：优先用父组件传入的 prop，其次用本地镜像。
    // 用于初始化渲染按钮时，让「画布展示确认框」跟随已选状态。
    resolveEffectiveSelectedIds (propIds, mirrorIds) {
      const fromProp = Array.isArray(propIds) ? propIds : []
      if (fromProp.length > 0) {
        return fromProp
      }
      return Array.isArray(mirrorIds) ? mirrorIds : []
    },

    buildFrontendLogicPointList (pointList = []) {
      const normalizedList = Array.isArray(pointList) ? pointList : []
      const visibleLogicPoints = normalizedList
        .filter(point => Number(point.id) !== LEGACY_NEBULA_LOGIC_POINT_ID)
        .map(point => ({ ...point }))

      if (!visibleLogicPoints.some(point => Number(point.id) === FRONTEND_DUMMY_YOUYI_POINT_ID)) {
        visibleLogicPoints.push({
          id: FRONTEND_DUMMY_YOUYI_POINT_ID,
          name: FRONTEND_DUMMY_YOUYI_POINT_NAME
        })
      }

      return visibleLogicPoints
    },

    formatAlarmInfoItem (alarmInfo = {}) {
      const directText = [alarmInfo.message, alarmInfo.content, alarmInfo.description, alarmInfo.alarmText]
        .find(value => value !== undefined && value !== null && `${value}`.trim())
      if (directText) {
        return `${directText}`.trim()
      }

      return [
        alarmInfo.planetName,
        alarmInfo.phaseName || alarmInfo.aspectName || alarmInfo.ruleName,
        alarmInfo.angle,
        alarmInfo.degree
      ]
        .filter(value => value !== undefined && value !== null && `${value}`.trim())
        .join(' ')
        .trim()
    },

    buildStarAlarmSummary (star = {}) {
      const alarmInfoList = Array.isArray(star.alarmInfoList)
        ? star.alarmInfoList.map(item => this.formatAlarmInfoItem(item)).filter(Boolean)
        : []

      const fallbackAlarmText = [
        star.alarmSummary,
        star.alarmText,
        star.alarmMessage,
        star.alarmDescription,
        star.alarmDesc
      ]
        .find(value => value !== undefined && value !== null && `${value}`.trim())

      const hasAlarm = Boolean(star.alarm) || alarmInfoList.length > 0 || Boolean(fallbackAlarmText)

      if (!hasAlarm) {
        return {
          hasAlarm: false,
          blinkAlarm: false,
          alarmSummary: ''
        }
      }

      const alarmSummary = alarmInfoList.join('；') || `${fallbackAlarmText || ''}`.trim()

      return {
        hasAlarm: true,
        blinkAlarm: Boolean(star.alarm),
        alarmSummary
      }
    },

    initializeLogicPoints (pointList, skipGenerate = false) {
      const selectedIds = this.resolveEffectiveSelectedIds(this.selectedPointIds, this.defaultSelectedPointIds)
      this.allLogicPoints = this.buildFrontendLogicPointList(pointList).map(point => {
        // 福点（幸运点）必须默认选中
        const selected = selectedIds.includes(point.id) || this.isFortunePoint(point.name)
        return {
          id: point.id,
          name: point.name,
          selected,
          // 画布展示确认框：渲染按钮时跟随该逻辑点的已选状态（已选=勾选），
          // 用户可再手动取消勾选。勾选状态只影响画布绘制，
          // 选中列表、请求参数等均不受影响。
          visibleOnCanvas: selected
        }
      })
      // 初始化完成后，自动更新选中的逻辑点到父组件（如果不是在初始化阶段）
      if (!skipGenerate && this.initialized) {
        this.$nextTick(() => {
          this.saveLogicPointConfig(true)
        })
      }
    },
    // 判断是否为福点（幸运点）
    isFortunePoint (name) {
      if (!name) return false
      const lowerName = name.toLowerCase()
      return this.fortunePointNames.some(fn =>
        lowerName.includes(fn.toLowerCase()) || name.includes(fn)
      )
    },
    // 该行星分类是否不应在画布上绘制。
    // 口径：只有「画布展示确认框」显式勾选(true) 才绘制，未勾选(false/缺省) 一律停止绘制。
    // 仅在绘制期过滤，选中状态、相位连线、容许度、请求参数等其它方面都不受任何影响。
    isPlanetHiddenOnCanvas (planetId) {
      const planet = this.allPlanets.find(p => p.id === planetId)
      return !planet || planet.visibleOnCanvas !== true
    },

    // 弹窗「确定」后按需重绘：隐藏分类只影响绘制，无需重新请求后端
    applyCanvasVisibility () {
      this.$nextTick(() => {
        this.scheduleDraw()
      })
    },

    // 该恒星是否不应在画布上绘制。口径与行星一致：
    // 只有「画布展示确认框」显式勾选(true) 才绘制，未勾选(false/缺省) 一律停止绘制。
    // 仅在绘制期过滤，选中列表、容许度、请求参数等其它方面都不受影响。
    isStarHiddenOnCanvas (star) {
      if (!star) return false
      // 后端 starPositions 的编号字段是 starNum，恒星列表用 id（同源），
      // 这里三种字段都兼容，最后再用名称兜底匹配。
      const rawId = star.starId !== undefined && star.starId !== null
        ? star.starId
        : (star.id !== undefined && star.id !== null ? star.id : star.starNum)
      let item = null
      if (rawId !== undefined && rawId !== null) {
        item = this.allStars.find(s => s.id === rawId)
      }
      if (!item && star.starName) {
        item = this.allStars.find(s => s.name === star.starName)
      }
      // 匹配不到时按可见处理，避免因字段差异把恒星整体清空
      return !!item && item.visibleOnCanvas !== true
    },
    filterCanvasVisibleStars (stars) {
      if (!Array.isArray(stars)) return []
      return stars.filter(star => !this.isStarHiddenOnCanvas(star))
    },

    // 该逻辑点是否不应在画布上绘制。口径与行星/恒星一致：
    // 只有「画布展示确认框」显式勾选(true) 才绘制，未勾选(false/缺省) 一律停止绘制。
    // 仅在绘制期过滤，选中列表、请求参数等其它方面都不受影响。
    isLogicPointHiddenOnCanvas (point) {
      if (!point) return false
      // 后端 logicPoints 的编号字段可能是 index 或 id，两种都兼容，最后用名称兜底匹配
      let rawId = point.index
      if (rawId === undefined || rawId === null) {
        rawId = point.id
      }
      let item = null
      if (rawId !== undefined && rawId !== null) {
        const targetId = Number(rawId)
        if (!Number.isNaN(targetId)) {
          item = this.allLogicPoints.find(p => Number(p.id) === targetId)
        }
      }
      if (!item && point.name) {
        item = this.allLogicPoints.find(p => p.name === point.name)
      }
      // 匹配不到时按可见处理，避免因字段差异把逻辑点整体清空
      return !!item && item.visibleOnCanvas !== true
    },
    filterCanvasVisibleLogicPoints (points) {
      if (!Array.isArray(points)) return []
      return points.filter(point => !this.isLogicPointHiddenOnCanvas(point))
    },

    // 保存行星配置
    savePlanetConfig (selectedPlanetIds = null, skipAutoSave = false) {
      const resolvedPlanetIds = Array.isArray(selectedPlanetIds)
        ? selectedPlanetIds
        : this.allPlanets.filter(p => p.selected).map(p => p.id)

      // 发送选中的行星ID列表到父组件
      this.$emit('update-planets', resolvedPlanetIds)
      if (!skipAutoSave) {
        this.$nextTick(() => {
          this.$emit('request-save-config')
        })
        this.showPlanetConfig = false
      }
    },

    saveStarConfig (selectedStarIds = null) {
      const resolvedStarIds = Array.isArray(selectedStarIds)
        ? selectedStarIds
        : this.selectedStars.map(s => s.id)

      // 发送选中的恒星列表到父组件
      this.$emit('update-stars', resolvedStarIds)
      this.$nextTick(() => {
        this.$emit('request-save-config')
      })
      this.showStarConfig = false
    },

    saveLogicPointConfig (selectedPointIds = null, skipAutoSave = false) {
      const resolvedPointIds = Array.isArray(selectedPointIds)
        ? selectedPointIds
        : this.selectedPoints.map(p => p.id)

      // 发送选中的逻辑点列表到父组件
      this.$emit('update-points', resolvedPointIds)
      if (!skipAutoSave) {
        this.$nextTick(() => {
          this.$emit('request-save-config')
        })
        this.showPointConfig = false
      }
    },
    // 保存催贵公式配置
    saveMidpointConfig (midpointPairs = []) {
      // 发送选中的中点对到父组件，用于刷新星盘
      this.$emit('update-midpoints', midpointPairs)
      this.$nextTick(() => {
        this.$emit('request-save-config')
      })
    },

    /**
     * 初始化画布
     */
    initCanvas () {
      if (!this.$refs.canvas) return

      const ctx = this.$refs.canvas.getContext('2d')
      // 画布位置微调：星盘整体上移 10px、右移 10px。
      // 只挪绘制中心而不用挪 canvas 元素：绘制与 hover / 点击命中判定共用 this.center，
      // 改中心点两者自动一致，不会出现「看着移了、点击还停在原位」的错位。
      const chartOffsetX = 10
      const chartOffsetY = -10
      this.center = {
        x: this.canvasSize / 2 + chartOffsetX,
        y: this.canvasSize / 2 + chartOffsetY
      }
      this.radius = this.canvasSize / 2 - 80

      // 创建渲染器实例
      this.renderer = new AstrolabeRenderer(
        ctx,
        this.center,
        this.radius,
        this.styles,
        this.zeroAngle
      )

      this.astrolabeDrawer = new AstrolabeDrawer(this.renderer)

      if (this.hasChartData()) {
        try {
          this.drawAstrolabe()
          this.emitRenderComplete()
        } catch (error) {
          console.error('[AstrolabeCanvas] 初始化绘制失败:', error)
          this.clearCanvas()
        }
      } else {
        // 初始就没有数据（例如账号无星盘权限）：保持空画布，
        // 不再先画一个白色底圆再去等数据
        this.clearCanvas()
      }
    },

    /**
     * 把指针事件的视口坐标换算成 canvas 画布坐标。
     *
     * 手机双指缩放页面后，getBoundingClientRect() 量到的是缩放后的 CSS 尺寸，
     * 与画布的绘制尺寸（canvasSize(+80) x canvasSize）不再相等；原先直接用
     * event.clientX - rect.left 会让 hover / 点击的命中判定整体错位。
     *
     * @param {MouseEvent|PointerEvent} event - 鼠标 / 指针事件
     * @returns {{x: number, y: number}} 画布坐标系中的位置
     */
    resolveCanvasPoint (event) {
      const canvasEl = this.$refs.canvas
      if (!canvasEl) {
        return { x: event.clientX, y: event.clientY }
      }

      const rect = canvasEl.getBoundingClientRect()
      const scaleX = rect.width > 0 ? canvasEl.width / rect.width : 1
      const scaleY = rect.height > 0 ? canvasEl.height / rect.height : 1

      return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY
      }
    },

    // 新增专用事件处理方法
    handleConfigButtonClick (event) {
    // 阻止事件冒泡到canvas-container
      event.stopPropagation()
    },

    resolveTooltipMovement (event) {
      const previous = this.lastMouseClientPosition
      const nextPosition = {
        x: event.clientX,
        y: event.clientY
      }
      const movement = previous
        ? {
          x: nextPosition.x - previous.x,
          y: nextPosition.y - previous.y
        }
        : { x: 0, y: 0 }

      this.lastMouseClientPosition = nextPosition

      if (movement.x !== 0 || movement.y !== 0) {
        this.lastPointerMovement = {
          x: movement.x,
          y: movement.y
        }
        return movement
      }

      return {
        x: this.lastPointerMovement.x || 0,
        y: this.lastPointerMovement.y || 0
      }
    },

    isMovingTowardTooltip (event, tooltipEl) {
      if (!this.tooltip.visible || !this.tooltip.position || !tooltipEl) {
        return false
      }

      const rect = tooltipEl.getBoundingClientRect()
      return isPointerInTooltipTransferZone({
        pointer: { x: event.clientX, y: event.clientY },
        anchor: this.tooltip.position,
        tooltipRect: {
          left: rect.left,
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom
        }
      })
    },

    handleCanvasPointerMove (event) {
      const tooltipMovement = this.resolveTooltipMovement(event)
      if (!this.isHoverTooltipMode) {
        return
      }

      this.handleCanvasHover(event, {
        interactionMode: 'hover',
        tooltipMovement,
        shouldLockTransferZone: true
      })
    },

    handleCanvasMouseLeave (event) {
      if (!this.isHoverTooltipMode || this.isEditingRemark) {
        return
      }

      const tooltipEl = this.$refs.tooltipElement ? this.$refs.tooltipElement.$el : this.$el.querySelector('.tooltip')
      if (tooltipEl && this.isMovingTowardTooltip(event, tooltipEl)) {
        clearTimeout(this.tooltipTimer)
        return
      }

      this.hideTooltip()
    },

    handleCanvasClick (event) {
      const tooltipMovement = this.resolveTooltipMovement(event)
      if (!this.isClickTooltipMode) {
        return
      }

      this.handleCanvasHover(event, {
        interactionMode: 'click',
        tooltipMovement,
        shouldLockTransferZone: false
      })
    },

    // 处理鼠标移动事件
    handleCanvasHover (event, options = {}) {
      const interactionMode = this.normalizeTooltipInteractionMode(options.interactionMode || this.resolvedTooltipInteractionMode)
      const shouldLockTransferZone = options.shouldLockTransferZone !== undefined
        ? options.shouldLockTransferZone
        : interactionMode === 'hover'

      // 添加配置面板区域检查
      const configPanel = this.$el.querySelector('.config-panel')
      if (configPanel && configPanel.contains(event.target)) {
        return // 点击的是配置面板区域，不执行后续逻辑
      }

      // 添加Tooltip区域检查（防止鼠标移入Tooltip时关闭）
      const tooltipEl = this.$refs.tooltipElement ? this.$refs.tooltipElement.$el : this.$el.querySelector('.tooltip')
      if (tooltipEl && tooltipEl.contains(event.target)) {
        return
      }

      const tooltipMovement = options.tooltipMovement || this.resolveTooltipMovement(event)

      // 鼠标正从星点移动向 tooltip 时，暂时锁定当前 tooltip，避免路过其他星时内容被替换
      if (shouldLockTransferZone && this.isMovingTowardTooltip(event, tooltipEl)) {
        clearTimeout(this.tooltipTimer)
        return
      }

      // 如果正在拖动悬浮表格，则不处理画布悬停
      if (this.isDraggingScoreTable) {
        return
      }
      // 清除之前的定时器
      clearTimeout(this.tooltipTimer)

      const { x: mouseX, y: mouseY } = this.resolveCanvasPoint(event)

      // 避免重复触发相同星星的悬停计算，可以留空或者只处理新加入
      let hasHoverItems = false
      const tempHoverItems = []

      // 检查行星和逻辑点
      if (this.astrolabeDrawer.planetPositionsLookup && this.astrolabeDrawer.planetPositionsLookup.length > 0) {
        this.astrolabeDrawer.planetPositionsLookup.forEach(item => {
          const dx = mouseX - item.center.x
          const dy = mouseY - item.center.y
          const distance = Math.sqrt(dx * dx + dy * dy)

          if (distance < item.radius) {
            const data = item.data // 统一使用 data 字段

            // 处理中点的悬浮显示
            if (item.type === 'midpoint') {
              const name = data.name || data.shortName || ''
              const displayName = name.replace(/-/g, '·')

              // 绝对黄经统一读取 geometry 主链
              const absoluteLong = normalizeAngle(
                data?.geometry?.absoluteLongitude ?? data?.absoluteLongitude
              ) ?? 0

              // 判断是否是地平盘
              const isHorizon = this.info.astrolabeType === '地平盘'
              let positionDetails = ''
              if (isHorizon) {
                // 地平盘显示方位角和高度
                const azimuthStr = data.horizontalAzimuth !== undefined ? data.horizontalAzimuth.toFixed(1) + '°' : ''
                const altitude = data.horizontalAltitude !== undefined ? data.horizontalAltitude.toFixed(1) + '°' : ''
                positionDetails = ` 方位角:${azimuthStr} 高度:${altitude}`
              } else {
                // 其他盘显示星座和度数
                positionDetails = this.formatSignPosition(data.signName, data.signOffDeg, data.signOffMin, data.signOffSec)
              }

              tempHoverItems.push({
                id: data.id || `midpoint-${data.name}`,
                name: displayName,
                color: '#9b59b6',
                details: `${positionDetails}${this.formatEclipticLatitudeDetail(data)}`,
                type: 'midpoint',
                angle: item.angle, // 用于绘制角度排序
                absoluteLong: absoluteLong, // 用于逻辑排序
                azimuth: data.horizontalAzimuth || 0,
                remark: this.resolvePersistedRemark({ type: 'midpoint', id: data.id || `midpoint-${data.name}` }, data.remark)
              })
            } else {
              // 处理行星和逻辑点
              let details = ''
              if (data.speed === '+') {
                details += ' 疾行'
              } else if (data.speed === '-') {
                details += ' 缓慢'
              }
              if (data.direction === 'Px') {
                details += ' 逆行'
              }

              // 绝对黄经统一读取 geometry 主链
              const absoluteLong = normalizeAngle(
                data?.geometry?.absoluteLongitude ?? data?.absoluteLongitude
              ) ?? 0
              // 获取方位角用于排序
              const azimuth = data.horizontalAzimuth || 0

              // 判断是否是地平盘
              const isHorizon = this.info.astrolabeType === '地平盘'
              let positionDetails = ''
              if (isHorizon) {
                // 地平盘显示方位角和高度
                const azimuthStr = data.horizontalAzimuth !== undefined ? data.horizontalAzimuth.toFixed(1) + '°' : ''
                const altitude = data.horizontalAltitude !== undefined ? data.horizontalAltitude.toFixed(1) + '°' : ''
                positionDetails = ` 方位角:${azimuthStr} 高度:${altitude}`
              } else {
                // 其他盘显示星座和度数
                positionDetails = this.formatSignPosition(data.signName, data.signOffDeg, data.signOffMin, data.signOffSec)
              }

              // 黄纬：行星数据里由接口提供（eclipticLatitudeJ2000）；黄经不再显示（盘面已按黄经落位，重复）
              const eclipticDetails = this.formatEclipticLatitudeDetail(data)

              tempHoverItems.push({
                id: data.id || data.index,
                name: data.fullName, // 单独提取用于加粗
                color: data.color, // 传递颜色
                details: `${positionDetails}${details}${eclipticDetails}`,
                type: item.type,
                angle: item.angle, // 用于绘制角度排序
                absoluteLong: absoluteLong, // 用于逻辑排序
                azimuth: azimuth, // 用于地平盘排序
                remark: this.resolvePersistedRemark({ type: item.type, id: data.id || data.index }, data.remark)
              })
            }

            hasHoverItems = true
          }
        })
      }

      // 2. 检查相位（新增）
      if (this.astrolabeDrawer.aspectPositions && this.astrolabeDrawer.aspectPositions.length > 0 && !hasHoverItems) {
        this.astrolabeDrawer.aspectPositions.forEach(item => {
          const dx = mouseX - item.center.x
          const dy = mouseY - item.center.y
          const distance = Math.sqrt(dx * dx + dy * dy)

          if (distance < item.radius) {
            // 获取两个行星的信息
            const planet1 = this.astrolabeDrawer.findPlanetById(this.astrolabeData.planets, item.phase.planet1Index)
            const planet2 = this.astrolabeDrawer.findPlanetById(this.astrolabeData.planets, item.phase.planet2Index)

            // 格式化相位信息
            const dms = CanvasUtils.formatAngleToDMS(item.phase.actualAngleDiff)
            const ruleSymbol = item.phase.ruleSymbol || this.astrolabeDrawer.getDefaultSymbol(item.phase.ruleIndex)

            tempHoverItems.push({
              name: `${planet1.shortName}${ruleSymbol}${planet2.shortName} `,
              displayText: ` ${dms}`,
              color: this.styles.phaseSymbol.colors[item.phase.ruleIndex] || '#9b59b6',
              type: 'aspect',
              angle: item.angle // 使用 Drawer 预存的排序角度
            })
            hasHoverItems = true
          }
        })
      }

      // 3. 检查星宿扇环（新增）
      if (!hasHoverItems && this.astrolabeDrawer.constellationSectors) {
        const { x: mouseX, y: mouseY } = this.resolveCanvasPoint(event)

        const dx = mouseX - this.center.x
        const dy = mouseY - this.center.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        // 计算鼠标角度（考虑零度基准）
        let angle = 360 - Math.atan2(dy, dx) * 180 / Math.PI
        angle = (angle + 360 - this.zeroAngle) % 360

        for (const sector of this.astrolabeDrawer.constellationSectors) {
          const isInDistance = dist >= sector.innerRadius && dist <= sector.outerRadius
          const isInAngle = CanvasUtils.isAngleBetween(
            angle,
            sector.startAngle,
            sector.endAngle
          )

          if (isInDistance && isInAngle) {
            tempHoverItems.push({
              id: `constellation-${sector.xingxiuName}`,
              name: sector.xingxiuName,
              details: `${this.formatSignPosition(sector.signName, sector.signOffDeg, sector.signOffMin, sector.signOffSec)}${this.formatEclipticLatitudeDetail(sector)}`,
              type: 'constellation',
              remark: this.resolvePersistedRemark({ type: 'constellation', id: `constellation-${sector.xingxiuName}` }, sector.remark),
              angle: CanvasUtils.normalizeAngle((sector.startAngle + sector.endAngle) / 2)
            })
            hasHoverItems = true
            break
          }
        }
      }

      // 新增：检查恒星（在星宿检测之后）
      if (this.astrolabeDrawer.starPositions?.length > 0) {
        const stars = this.astrolabeDrawer.findStarAtPosition(mouseX, mouseY)
        if (stars.length > 0) {
          // tooltip 绝对黄经优先读取 geometry 主链，避免再从旧绘制角度反推
          const isHorizon = this.info.astrolabeType === '地平盘'
          // 「点报警」权限：决定悬浮框是否展示报警样式（红底 + 闪烁）
          const canShowPointAlarm = getAlarmEntitlement().pointAlarm
          stars.forEach(starPos => {
            const star = starPos.star
            const absoluteLong = normalizeAngle(
              star?.geometry?.absoluteLongitude ?? star?.absoluteLongitude ?? star?.starRLng
            ) ?? 0
            // 获取方位角用于排序
            const azimuth = star.horizontalAzimuth || 0
            const alarmState = this.buildStarAlarmSummary(star)
            let starDetails = ''
            if (isHorizon) {
              // 地平盘显示方位角和高度
              const azimuthStr = star.horizontalAzimuth !== undefined ? star.horizontalAzimuth.toFixed(1) + '°' : ''
              const altitude = star.horizontalAltitude !== undefined ? star.horizontalAltitude.toFixed(1) + '°' : ''
              starDetails = ` 方位角:${azimuthStr} 高度:${altitude}`
            } else {
              // 其他盘显示星座和度数
              starDetails = this.formatSignPosition(star.starInSignName, star.signOffDeg, star.signOffMin, star.signOffSec).trimStart()
            }
            // 恒星只补黄纬（接口暂未提供恒星黄纬时取不到就不显示）；黄经同样不再显示
            starDetails += this.formatEclipticLatitudeDetail(star)

            tempHoverItems.push({
              id: star.starId || star.id || `star-${star.starName}`,
              name: star.starName,
              color: star.starColor || '#000',
              details: starDetails,
              type: 'star',
              angle: starPos.angle, // 用于排序
              absoluteLong: absoluteLong, // 用于逻辑排序
              azimuth: azimuth, // 用于地平盘排序
              // 无「点报警」权限时不展示任何报警样式
              hasAlarm: alarmState.hasAlarm && canShowPointAlarm,
              blinkAlarm: alarmState.blinkAlarm && canShowPointAlarm,
              alarmSummary: alarmState.alarmSummary,
              remark: this.resolvePersistedRemark({ type: 'star', id: star.starId || star.id || `star-${star.starName}` }, star.remark)
            })
          })
          hasHoverItems = true
        }
      }

      // 新增：检查特殊轴（ASC/MC）- 支持圆形和矩形区域检测
      if (this.astrolabeDrawer.specialAxisPositions?.length > 0) {
        this.astrolabeDrawer.specialAxisPositions.forEach(item => {
          let isHit = false

          if (item.rectWidth !== undefined) {
            // 矩形区域检测（轴线位置）
            const rectWidth = item.rectWidth
            const rectLength = item.rectLength || 120
            const zeroAngle = this.renderer ? this.renderer.zeroAngle : 180
            const angleRad = CanvasUtils.degToRad(item.angle, zeroAngle)
            const halfWidth = rectWidth / 2
            const halfLength = rectLength / 2

            const dx = mouseX - item.center.x
            const dy = mouseY - item.center.y

            // Inverse rotation to align the axis with the x-axis
            // angleRad is the angle of the axis in radians
            const cos = Math.cos(angleRad)
            const sin = Math.sin(angleRad)

            // Projecting (dx, dy) onto axis direction and perpendicular direction
            const rotatedX = dx * cos + dy * sin
            const rotatedY = -dx * sin + dy * cos

            // rectWidth is the length along the axis (e.g. 120), rectLength is the thickness (e.g. 12)
            // So halfWidth = 60, halfLength = 6
            isHit = Math.abs(rotatedX) <= halfWidth && Math.abs(rotatedY) <= halfLength
          }

          if (isHit) {
            hasHoverItems = true
            const pointsReal = this.astrolabeDrawer.pointsReal || {}

            if (item.type === 'ASC' && pointsReal.ASC !== undefined) {
              let ascPlanet = null
              if (this.astrolabeData && this.astrolabeData.planets) {
                ascPlanet = this.astrolabeData.planets.find(p => p.index === 1000015)
              }
              const dms = ascPlanet
                ? this.formatSignPosition(ascPlanet.signName, ascPlanet.signOffDeg, ascPlanet.signOffMin, ascPlanet.signOffSec)
                : CanvasUtils.formatAngleToDMS(pointsReal.ASC)
              // ASC 是轴点，没有黄纬概念，这里只显示星座度分秒，不拼黄纬
              tempHoverItems.push({
                name: 'Asc',
                color: '#ff0000',
                details: `${dms}`,
                type: 'axis',
                angle: item.angle // 用于排序
              })
            } else if (item.type === 'MC' && pointsReal.ARMC !== undefined) {
              let mcPlanet = null
              if (this.astrolabeData && this.astrolabeData.planets) {
                mcPlanet = this.astrolabeData.planets.find(p => p.index === 1000014)
              }
              const dms = mcPlanet
                ? this.formatSignPosition(mcPlanet.signName, mcPlanet.signOffDeg, mcPlanet.signOffMin, mcPlanet.signOffSec)
                : CanvasUtils.formatAngleToDMS(pointsReal.ARMC)
              // MC 同 ASC：轴点没有黄纬概念，只显示星座度分秒
              tempHoverItems.push({
                name: 'Mc',
                color: '#0000ff',
                details: `${dms}`,
                type: 'axis',
                angle: item.angle // 用于排序
              })
            }

            // 处理本命盘特殊点
            const natalPointsReal = this.natalAstrolabeData && this.natalAstrolabeData.specialPointsReal ? this.natalAstrolabeData.specialPointsReal : {}

            if (item.type === 'NatalASC' && natalPointsReal.ASC !== undefined) {
              let ascPlanet = null
              if (this.natalAstrolabeData && this.natalAstrolabeData.planets) {
                ascPlanet = this.natalAstrolabeData.planets.find(p => p.index === 1000015)
              }
              const dms = ascPlanet
                ? this.formatSignPosition(ascPlanet.signName, ascPlanet.signOffDeg, ascPlanet.signOffMin, ascPlanet.signOffSec)
                : CanvasUtils.formatAngleToDMS(natalPointsReal.ASC)
              // 本命盘 ASC 同样不显示黄纬（轴点没有黄纬概念）
              tempHoverItems.push({
                name: 'Asc',
                color: '#ff0000',
                details: `${dms}`,
                type: 'axis',
                angle: item.angle // 用于排序
              })
            } else if (item.type === 'NatalMC' && natalPointsReal.ARMC !== undefined) {
              let mcPlanet = null
              if (this.natalAstrolabeData && this.natalAstrolabeData.planets) {
                mcPlanet = this.natalAstrolabeData.planets.find(p => p.index === 1000014)
              }
              const dms = mcPlanet
                ? this.formatSignPosition(mcPlanet.signName, mcPlanet.signOffDeg, mcPlanet.signOffMin, mcPlanet.signOffSec)
                : CanvasUtils.formatAngleToDMS(natalPointsReal.ARMC)
              // 本命盘 MC 同样不显示黄纬（轴点没有黄纬概念）
              tempHoverItems.push({
                name: 'Mc',
                color: '#0000ff',
                details: `${dms}`,
                type: 'axis',
                angle: item.angle // 用于排序
              })
            }
          }
        })
      }

      // 新增：检查宫头星座标签
      if (this.astrolabeDrawer.signLabelPositions?.length > 0) {
        this.astrolabeDrawer.signLabelPositions.forEach(item => {
          const dx = mouseX - item.center.x
          const dy = mouseY - item.center.y
          const distance = Math.sqrt(dx * dx + dy * dy)

          if (distance < item.radius) {
            const h = item.house || {}
            const signName = item.signName || h.signName
            const signId = item.signIndex !== undefined && item.signIndex !== null && item.signIndex !== '' ? item.signIndex : signName
            tempHoverItems.push({
              id: `sign-label-${signId}`,
              name: signName,
              details: `${item.house ? this.formatSignPosition('', h.signOffDeg, h.signOffMin, h.signOffSec) : ''}${this.formatEclipticLatitudeDetail(h)}`,
              color: this.astrolabeDrawer.styles.signCircle.fontColor[item.signIndex],
              type: 'signLabel',
              remark: this.resolvePersistedRemark({ type: 'signLabel', signIndex: item.signIndex, signName }, item.remark || h.signRemark),
              signIndex: item.signIndex,
              signName,
              angle: item.angle // 用于排序
            })
            hasHoverItems = true
          }
        })
      }

      // 新增：检查后天宫扇形
      if (!hasHoverItems && this.astrolabeDrawer.houseSectorPositions?.length > 0) {
        const dx = mouseX - this.center.x
        const dy = mouseY - this.center.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        // 计算鼠标角度（考虑零度基准）
        const zeroAngle = this.renderer ? this.renderer.zeroAngle : 180
        let angle = 360 - Math.atan2(dy, dx) * 180 / Math.PI
        angle = (angle + 360 - zeroAngle) % 360

        for (const sector of this.astrolabeDrawer.houseSectorPositions) {
          const isInDistance = dist >= sector.innerRadius && dist <= sector.outerRadius
          const isInAngle = CanvasUtils.isAngleBetween(
            angle,
            sector.startAngle,
            sector.endAngle
          )

          if (isInDistance && isInAngle) {
            const h = sector.house
            tempHoverItems.push({
              id: `house-sector-${h.houseNumber}`,
              name: `第${h.houseNumber}宫`,
              details: this.formatEclipticLatitudeDetail(h),
              type: 'houseSector',
              remark: this.resolvePersistedRemark({ type: 'houseSector', houseNumber: h.houseNumber }, h.remark),
              houseNumber: h.houseNumber,
              angle: CanvasUtils.normalizeAngle((sector.startAngle + sector.endAngle) / 2)
            })
            hasHoverItems = true
            break
          }
        }
      }

      // 4. 独立检查天运圆点，避免被先天宫名称或宫位扇区遮挡命中
      if (this.astrolabeDrawer.tianyunPositions?.length > 0 && this.hasPermission('tianyun')) {
        this.astrolabeDrawer.tianyunPositions.forEach(item => {
          const dx = mouseX - item.center.x
          const dy = mouseY - item.center.y
          const distance = Math.sqrt(dx * dx + dy * dy)

          if (distance < item.radius) {
            const data = item.data
            tempHoverItems.push({
              id: `tianyun-${data.year}-${data.yunIndex}`,
              name: data.name, // "2020天运"
              color: data.color || '#000',
              details: `${this.formatSignPosition(data.signName, data.signOffDeg, data.signOffMin, data.signOffSec)}${this.formatEclipticLatitudeDetail(data)}`,
              type: 'tianyun',
              angle: item.angle,
              remark: this.resolvePersistedRemark({ type: 'tianyun', id: `tianyun-${data.year}-${data.yunIndex}` }, data.remark)
            })
            hasHoverItems = true
          }
        })
      }

      // 如果有悬停元素，显示tooltip
      if (hasHoverItems) {
        // 如果正在编辑备注，不更新 tooltip 位置和内容，也不隐藏
        if (this.isEditingRemark) return

        // 如果任何对话框打开，隐藏 tooltip 并返回
        if (this.isAnyDialogOpen) {
          this.hideTooltip(true) // 强制隐藏
          return
        }

        clearTimeout(this.tooltipTimer) // 清除隐藏定时器
        this.hoverItems = tempHoverItems // 全盘更新悬停项数据

        // 【核心排序逻辑】统一使用 angle (绘制角度) 升序排列
        // 逆序以满足用户的排版需求
        this.hoverItems.sort((a, b) => {
          const angleA = a.angle !== undefined ? a.angle : 0
          const angleB = b.angle !== undefined ? b.angle : 0
          return angleA - angleB
        })

        // 检查悬浮的元素是否发生改变
        const currentIds = this.hoverItems.map(i => i.id || i.name).sort().join(',')
        const prevIds = (this.tooltip.items || []).map(i => i.id || i.name).sort().join(',')

        if (!this.tooltip.visible || currentIds !== prevIds || interactionMode === 'click') {
          this.tooltip = {
            visible: true,
            position: { x: event.clientX, y: event.clientY },
            movement: tooltipMovement,
            items: this.hoverItems
          }
        } else {
          // 如果悬停的元素没变且 tooltip 已显示，则保持原位置不动，仅更新内容
          this.tooltip.visible = true
          this.tooltip.movement = tooltipMovement
          this.tooltip.items = this.hoverItems
        }
      } else {
        if (this.tooltip.visible && !this.isEditingRemark) {
          this.hideTooltip(true)
        }
      }
    },

    // 鼠标离开时隐藏tooltip
    hideTooltip (force = false) {
      this.lastMouseClientPosition = null
      clearTimeout(this.tooltipTimer)
      if (this.isEditingRemark && !force) return
      if (this.tooltip.visible) {
        this.tooltip.visible = false
        this.hoverItems = []
      }
    },

    handleTooltipEnter () {
      clearTimeout(this.tooltipTimer)
    },

    handleTooltipLeave () {
      clearTimeout(this.tooltipTimer)
      if (!this.isHoverTooltipMode || this.isEditingRemark) {
        return
      }
      this.tooltipTimer = setTimeout(() => {
        if (!this.isEditingRemark) {
          this.hideTooltip()
        }
      }, 120)
    },

    buildTooltipRemarkStorageKey (item) {
      return buildTooltipRemarkStorageKey(item)
    },

    resolvePersistedRemark (item, fallbackRemark = '') {
      const storageKey = this.buildTooltipRemarkStorageKey(item)
      if (storageKey && this.tooltipRemarkMap && this.tooltipRemarkMap[storageKey] !== undefined) {
        return this.tooltipRemarkMap[storageKey]
      }
      return fallbackRemark || ''
    },

    handleSaveRemark (updatedItem) {
      // 1. 更新当前显示的 tooltip 中的 hoverItems
      const localItem = this.hoverItems.find(i => i.id === updatedItem.id && i.type === updatedItem.type)
      if (localItem) {
        localItem.remark = updatedItem.remark
      }

      // 2. 更新底层的 astrolabeData，保证下次 hover 时显示最新的 remark
      if (this.astrolabeData && this.astrolabeData.planets && (updatedItem.type === 'planet' || updatedItem.type === 'point')) {
        const planetMatches = this.astrolabeData.planets.filter(p => p.id === updatedItem.id || p.index === updatedItem.id)
        planetMatches.forEach(p => { this.$set(p, 'remark', updatedItem.remark) })
      }
      if (this.astrolabeData && this.astrolabeData.logicPoints && updatedItem.type === 'point') {
        const logicMatches = this.astrolabeData.logicPoints.filter(p => p.id === updatedItem.id || p.index === updatedItem.id)
        logicMatches.forEach(p => { this.$set(p, 'remark', updatedItem.remark) })
      }
      if (this.astrolabeData && this.astrolabeData.starPositions && updatedItem.type === 'star') {
        const starMatches = this.astrolabeData.starPositions.filter(s => (s.starId || s.id || `star-${s.starName}`) === updatedItem.id)
        starMatches.forEach(s => { this.$set(s, 'remark', updatedItem.remark) })
      }
      if (this.astrolabeData && this.astrolabeData.midpoints && updatedItem.type === 'midpoint') {
        const midMatches = this.astrolabeData.midpoints.filter(m => (m.id || `midpoint-${m.name}`) === updatedItem.id)
        midMatches.forEach(m => { this.$set(m, 'remark', updatedItem.remark) })
      }
      if (this.astrolabeData && this.astrolabeData.xingxiuPositions && updatedItem.type === 'constellation') {
        const xxMatches = this.astrolabeData.xingxiuPositions.filter(x => `constellation-${x.xingxiuName}` === updatedItem.id)
        xxMatches.forEach(x => { this.$set(x, 'remark', updatedItem.remark) })
      }
      if (this.astrolabeData && this.astrolabeData.houses && updatedItem.type === 'signLabel') {
        const signMatches = this.astrolabeData.houses.filter(h => h.signIndex === updatedItem.signIndex || h.signName === updatedItem.signName)
        signMatches.forEach(h => { this.$set(h, 'signRemark', updatedItem.remark) })
      }
      if (this.astrolabeData && this.astrolabeData.houses && updatedItem.type === 'houseSector') {
        const houseMatches = this.astrolabeData.houses.filter(h => h.houseNumber === updatedItem.houseNumber)
        houseMatches.forEach(h => { this.$set(h, 'remark', updatedItem.remark) })
      }
      if (this.astrolabeData && this.astrolabeData.tianyunPositions && updatedItem.type === 'tianyun') {
        const tianMatches = this.astrolabeData.tianyunPositions.filter(t => `tianyun-${t.year}-${t.yunIndex}` === updatedItem.id)
        tianMatches.forEach(t => { this.$set(t, 'remark', updatedItem.remark) })
      }
      this.$emit('update-remark', updatedItem)
      this.$emit('request-save-config')
    },

    handleEditingChange (isEditing) {
      this.isEditingRemark = isEditing
      if (isEditing) {
        // 开始编辑时，立即清除所有定时器，确保浮框万无一失不消失
        clearTimeout(this.tooltipTimer)
      }
    },

    /**
     * 主绘制方法 - 绘制整个星盘（黄道盘）
     */
    drawAstrolabe () {
      this.astrolabeDrawer.initDrawer(this.canvasSize)

      // 地平盘设置：0点在正下方，顺时针绘制
      // 其他盘：0点在正左方，逆时针绘制（默认）
      const isHorizon = this.info.astrolabeType === '地平盘'
      const horizonZeroAngle = 270 // 0点在正下方
      const normalZeroAngle = 180 // 0点在正左方

      // 【绘制方向分离】
      // 地平盘：0度在正下方（270度），方位角数据需要转换为逆时针绘制角度
      // 其他盘：0度在正左方（180度），使用黄道经度
      this.renderer.updateConfig({
        zeroAngle: isHorizon ? horizonZeroAngle : normalZeroAngle
      })

      // 绘制宫位
      const pointsReal = this.astrolabeData.specialPointsReal || {}
      const houses = this.astrolabeData.houses || []
      const planets = this.astrolabeData.planets || []
      const specialPoints = this.astrolabeData.specialPoints || {}
      const horizontalSpecialPoints = this.astrolabeData.horizontalSpecialPoints || {}
      const logicPoints = this.astrolabeData.logicPoints || []
      const xingxiuPositions = this.astrolabeData.xingxiuPositions || []
      const starPositions = this.astrolabeData.starPositions || []
      const phases = this.astrolabeData.phases || []
      const midpoints = this.astrolabeData.midpoints || []
      const referenceAsc = resolveChartAscAbsoluteLongitude(this.astrolabeData)
      this.astrolabeDrawer.setEhFlag(this.ehFlag)
      const preparedAstrolabeData = buildChartDrawData(this.astrolabeData, {
        referenceAscAbsoluteLongitude: referenceAsc,
        equalHouse: this.ehFlag
      })
      const preparedNatalAstrolabeData = this.natalAstrolabeData
        ? buildChartDrawData(this.natalAstrolabeData, {
          referenceAscAbsoluteLongitude: referenceAsc,
          // 总经理最新确认：本命盘显示仍相对主盘 ASC，
          // 但等分宫位模式下的宫内尺度缩放必须按主盘 houses 计算，
          // 因为本命盘当前显示的宫位格就是主盘宫位格。
          scaleChartAscAbsoluteLongitude: referenceAsc,
          scaleHouses: this.astrolabeData.houses || [],
          equalHouse: this.ehFlag
        })
        : null
      const drawPointsReal = preparedAstrolabeData.specialPointsReal || pointsReal
      const drawHouses = preparedAstrolabeData.houses || houses
      const drawPlanets = preparedAstrolabeData.planets || planets
      const drawSpecialPoints = preparedAstrolabeData.specialPoints || specialPoints
      const drawLogicPoints = preparedAstrolabeData.logicPoints || logicPoints
      const drawXingxiuPositions = preparedAstrolabeData.xingxiuPositions || xingxiuPositions
      const drawStarPositions = preparedAstrolabeData.starPositions || starPositions
      const drawPhases = preparedAstrolabeData.phases || phases
      const drawMidpoints = preparedAstrolabeData.midpoints || midpoints
      const drawTianyunPositions = preparedAstrolabeData.tianyunPositions || []

      // 设置特殊点数据（用于悬浮检测）
      this.astrolabeDrawer.setPointsReal(drawPointsReal)

      // 绘制先天宫格子（星座背景），地平盘不显示
      if (!isHorizon) {
        this.astrolabeDrawer.drawSignSectors(drawPointsReal)
      }

      if (this.showFixRing) {
        // 固定环模式：使用方位角坐标绘制
        this.drawAstrolabeFixRingMode(xingxiuPositions, planets, starPositions, logicPoints, midpoints, drawTianyunPositions, isHorizon)
        // 2026-10-03 总经理要求：地平盘隐藏 ASC/MC 两根轴线（开关见文件顶部）
        if (isHorizon && SHOW_HORIZON_SPECIAL_AXES) {
          this.astrolabeDrawer.drawHorizonSpecialAxes(horizontalSpecialPoints)
        }
        // 最后最外圈（确保在最外层）
        this.astrolabeDrawer.drawOuterRing()

        // 追加: 在固定环模式下也可以绘制本命盘外圈 (如地平黄道盘)
        if (preparedNatalAstrolabeData && preparedNatalAstrolabeData.houses && this.hasPermission('natal_chart')) {
          this.drawNatalOuterRing(preparedNatalAstrolabeData, drawHouses)
        }
      } else {
        // 正常模式：使用黄道经度坐标绘制
        this.drawAstrolabeNormalMode(drawXingxiuPositions, drawHouses, drawPointsReal, drawPlanets, drawStarPositions, drawLogicPoints, drawPhases, drawSpecialPoints, drawMidpoints, drawTianyunPositions)
        // 绘制黄道盘的本命盘外圈（如果存在本命盘数据）
        if (preparedNatalAstrolabeData && preparedNatalAstrolabeData.houses && this.hasPermission('natal_chart')) {
          this.drawNatalOuterRing(preparedNatalAstrolabeData, drawHouses)
        }
      }

      // 新增：最后绘制刻度圈（确保在最外层）
      // 赤道盘不显示刻度圈，其他盘根据showScaleCircle设置显示
      const isEquator = this.info.astrolabeType === '赤道盘'
      this.scaleStyles.show = isEquator ? false : this.showScaleCircle
      this.astrolabeDrawer.updateScaleStyles(this.scaleStyles)
      this.astrolabeDrawer.drawScaleCircle(this.showFixRing, drawPointsReal)
    },

    /**
     * 固定环模式绘制
     */
    drawAstrolabeFixRingMode (xingxiuPositions, planets, starPositions, logicPoints, midpoints, tianyunPositions = [], isHorizon) {
      // 清空位置数组
      // this.astrolabeDrawer.clearPositions()

      // 绘制以phaseCircle.radiusRatio为半径的圆圈
      this.astrolabeDrawer.drawPhaseCircle(isHorizon)
      // 绘制28星宿格子（地平盘不显示）
      if (!isHorizon) {
        this.astrolabeDrawer.drawConstellationsSectorFixRing(xingxiuPositions)
      }

      // 绘制相位连线（使用方位角信息），过滤只显示选中的相位
      if (this.astrolabeData.phases && this.astrolabeData.phases.length > 0) {
        const filteredPhases = this.filterPhases(this.astrolabeData.phases)
        this.astrolabeDrawer.drawPhaseLines(filteredPhases, planets, true, isHorizon)
      }

      // 2026-10-03 总经理要求：坐山指针只在地平盘显示，其余盘面一律隐藏
      // （开关 SHOW_ZUOSHAN_POINTER_ON_OTHER_CHARTS 见文件顶部，改回 true 即恢复旧行为）
      if (this.showZuoshanArrow &&
          this.zuoshan !== null && this.zuoshan !== undefined &&
         (this.info.astrolabeType === '地平盘' ||
          (SHOW_ZUOSHAN_POINTER_ON_OTHER_CHARTS && this.hasPermission('zuoshan_pointer')))) {
        this.astrolabeDrawer.drawZuoshanArrow(this.zuoshan)
      }

      // 在其他盘面显示后台返回的坐山投影数据（2026-10-03 起默认关闭）
      const projectedZuoshan = resolveProjectedZuoshan(this.astrolabeData)
      if (SHOW_ZUOSHAN_POINTER_ON_OTHER_CHARTS &&
          this.showZuoshanArrow &&
          this.info.astrolabeType !== '地平盘' &&
          projectedZuoshan !== null &&
          this.hasPermission('zuoshan_pointer')) {
        this.astrolabeDrawer.drawZuoshanArrow(projectedZuoshan)
      }

      // 绘制行星（过滤掉ASC和MC，以及不显示的虚拟点）
      // 追加：被「画布展示确认框」取消勾选的分类不绘制（仅此处过滤，phases/midpoints 仍用完整列表）
      const HIDDEN_IDS = new Set([1000017, 1000018, 1000019])
      const planetsWithoutAxis = planets.filter(p => p.index !== 1000014 && p.index !== 1000015 && !HIDDEN_IDS.has(p.index) && !this.isPlanetHiddenOnCanvas(p.index))
      this.astrolabeDrawer.drawPlanetsFixRing(planetsWithoutAxis)

      // 绘制逻辑点（过滤掉福点index=1000016，避免重复绘制）
      // 追加：被「画布展示确认框」取消勾选的逻辑点不绘制（仅此处过滤，其余逻辑不受影响）
      const logicPointsWithoutFortune = logicPoints.filter(p => p.index !== 1000016)
      this.astrolabeDrawer.drawPlanetsFixRing(this.filterCanvasVisibleLogicPoints(logicPointsWithoutFortune), false)

      // 绘制催贵公式中点
      if (midpoints && midpoints.length > 0 && this.hasPermission('midpoint_formula')) {
        this.astrolabeDrawer.drawMidpointsFixRing(midpoints, planets)
      }

      // 绘制恒星（使用原有角度信息）
      // 追加：被「画布展示确认框」取消勾选的恒星不绘制（仅此处过滤，其余逻辑不受影响）
      if (starPositions && starPositions.length > 0) {
        this.astrolabeDrawer.drawStarsFixRing(this.filterCanvasVisibleStars(starPositions))
      }

      // 绘制天运圆点
      if (tianyunPositions && tianyunPositions.length > 0 && this.hasPermission('tianyun')) {
        this.astrolabeDrawer.drawTianyun(tianyunPositions)
      }
    },

    /**
     * 正常模式绘制
     */
    drawAstrolabeNormalMode (xingxiuPositions, houses, pointsReal, planets, starPositions, logicPoints, phases, specialPoints, midpoints, tianyunPositions = []) {
      this.astrolabeDrawer.houses = houses
      // 绘制28星宿格子（星宿背景）
      this.astrolabeDrawer.drawConstellationsSector(xingxiuPositions, pointsReal)

      // 存储 houses 到 drawer 供等分宫位计算使用。
      this.astrolabeDrawer.houses = houses
      // 总经理当前口径下，主盘正常模式也必须沿用页面当前 ehFlag，
      // 不能在这里强制打回 false；否则后续主盘 drawSpecialAxis()
      // 会错误走“标准模式 MC”分支，导致主盘 MC 仍然斜掉。
      this.astrolabeDrawer.setEhFlag(this.ehFlag)
      const preparedPlanets = this.astrolabeDrawer.preparePlanetsWithSpecialAxisAngles(planets, specialPoints)

      const signLabelFlag = new Array(12).fill(0) // 标记星座位上是否有文字
      const renderedSignLabels = [] // 保留每个宫头标签实例，避免重复星座角度被覆盖

      for (let i = 0; i < houses.length; i++) {
        const house = houses[i]
        const startAngle = house.position
        const endAngle = houses[(i + 1) % 12].position
        const signIndex = house.signIndex

        // 绘制后天宫位扇形（宫位背景）
        this.astrolabeDrawer.drawHouseSector(startAngle, endAngle)

        // 在宫头位置绘制星座标签（使用新的固定角度）
        this.astrolabeDrawer.drawSignLabels(startAngle, house, signIndex)

        // 绘制宫位数字背景扇形
        this.astrolabeDrawer.drawHouseNumberSector(startAngle, endAngle, house)

        // 绘制宫位数字标签
        this.astrolabeDrawer.drawHouseNumberLabel(startAngle, endAngle, house)

        signLabelFlag[signIndex] = 1 // 标记signIndex星座已用
        renderedSignLabels.push({
          signIndex,
          angle: startAngle,
          houseNumber: house.houseNumber,
          signName: house.signName,
          remark: house.signRemark
        })
      }

      // 劫夺
      this.astrolabeDrawer.drawInterceptedLabels(signLabelFlag, pointsReal, houses, renderedSignLabels)

      // 绘制相位连线，过滤只显示选中的相位
      const filteredPhases = this.filterPhases(phases)
      this.astrolabeDrawer.drawPhaseLines(filteredPhases, preparedPlanets, false)

      // 绘制行星（过滤掉ASC和MC，以及不显示的虚拟点）
      // 追加：被「画布展示确认框」取消勾选的分类不绘制（仅此处过滤，phases/midpoints 仍用完整列表）
      const HIDDEN_IDS = new Set([1000017, 1000018, 1000019])
      const planetsWithoutAxis = preparedPlanets.filter(p => p.index !== 1000014 && p.index !== 1000015 && !HIDDEN_IDS.has(p.index) && !this.isPlanetHiddenOnCanvas(p.index))
      this.astrolabeDrawer.drawPlanets(planetsWithoutAxis)

      // 绘制逻辑点（过滤掉福点index=1000016，避免重复绘制）
      // 追加：被「画布展示确认框」取消勾选的逻辑点不绘制（仅此处过滤，其余逻辑不受影响）
      const logicPointsWithoutFortune = logicPoints.filter(p => p.index !== 1000016)
      this.astrolabeDrawer.drawPlanets(this.filterCanvasVisibleLogicPoints(logicPointsWithoutFortune), false)

      // 绘制催贵公式中点
      if (midpoints && midpoints.length > 0 && this.hasPermission('midpoint_formula')) {
        this.astrolabeDrawer.drawMidpoints(midpoints, preparedPlanets)
      }

      // 绘制恒星
      // 追加：被「画布展示确认框」取消勾选的恒星不绘制（仅此处过滤，其余逻辑不受影响）
      if (starPositions && starPositions.length > 0) {
        this.astrolabeDrawer.drawStars(this.filterCanvasVisibleStars(starPositions))
      }

      // 绘制天运圆点
      if (tianyunPositions && tianyunPositions.length > 0 && this.hasPermission('tianyun')) {
        this.astrolabeDrawer.drawTianyun(tianyunPositions)
      }

      // 绘制特殊轴线
      this.astrolabeDrawer.drawSpecialAxis(specialPoints)

      const projectedZuoshan = resolveProjectedZuoshan(this.astrolabeData)

      // 地平盘正常模式显示用户输入的坐山（2026-10-03 起坐山指针仅地平盘显示）
      if (this.showZuoshanArrow &&
          this.info.astrolabeType === '地平盘' &&
          this.zuoshan !== null && this.zuoshan !== undefined) {
        this.astrolabeDrawer.drawZuoshanArrow(this.zuoshan)
      }

      // 其他盘面显示后端返回的坐山投影（2026-10-03 总经理要求：默认关闭，开关见文件顶部）
      if (SHOW_ZUOSHAN_POINTER_ON_OTHER_CHARTS &&
          this.showZuoshanArrow &&
          this.info.astrolabeType !== '地平盘' &&
          projectedZuoshan !== null &&
          this.hasPermission('zuoshan_pointer')) {
        this.astrolabeDrawer.drawZuoshanArrow(projectedZuoshan)
      }
    },

    /**
     * 本命盘外圈绘制
     * 如果存在本命盘数据，在最外圈绘制本命盘行星（用于黄道盘）
     */
    drawNatalOuterRing (natalData = null, sharedMainHouses = null) {
      if (!natalData || !natalData.houses) {
        return
      }

      const planets = natalData.planets || []
      const natalHouses = natalData.houses || []
      const natalSpecialPoints = natalData.specialPoints || {}
      const originalHouses = this.astrolabeDrawer.houses
      this.astrolabeDrawer.houses = sharedMainHouses || this.astrolabeData.houses || originalHouses
      this.astrolabeDrawer.drawNatalHouseSector(natalHouses, planets, 0, natalSpecialPoints)
      this.astrolabeDrawer.houses = originalHouses
    },

    // 新增：悬浮表格拖动处理方法
    handleScoreTableDragStart (event, position) {
      this.isDraggingScoreTable = true
      // 记录起点快照，位移按「增量」计算，避免视口坐标与容器坐标混用导致的跳变
      this.scoreTableDragStart = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        posX: position.x,
        posY: position.y
      }
    },
    handleScoreTableDragMove (event) {
      if (!this.isDraggingScoreTable) return

      const start = this.scoreTableDragStart
      this.scoreTablePosition = {
        x: start.posX + (event.clientX - start.pointerX),
        y: start.posY + (event.clientY - start.pointerY)
      }
    },

    handleScoreTableDragEnd () {
      this.isDraggingScoreTable = false
      this.scoreTableDragStart = { pointerX: 0, pointerY: 0, posX: 0, posY: 0 }
    }
  }
}
</script>

<style scoped>
@keyframes blink {
  0%, 100% {border-color: red;}
  50% {
    box-shadow: inset 0 0 60px rgba(255, 0, 0, 0); /* 内边影 */
    border-color: transparent;
  }
}
.warning{
  box-shadow: inset 0 0 120px rgba(255, 0, 0, 0.6); /* 内边影 */
  transition: opacity 0.5s; /* 平滑过渡效果 */
  border: 1px solid red;
  padding: 10px;
  animation: blink 1s infinite;
}
.canvas-container {
  background: transparent !important; /* 容器透明 */
}

canvas {
  background-color: transparent !important; /* 画布透明 */
  /* 星盘图形并未铺满画布（上下各留有一段透明空白），用负外边距把这段空白吃掉，
     从而收紧「画布 / 外部白线边框」的上下边距，让底部操作栏完整露出。
     只改本元素的占位高度，绘制坐标、hover 命中（都基于 canvas 内部坐标）不受影响。 */
  margin-top: -22px;
  margin-bottom: -48px;
}

.loading {
  background: transparent !important; /* 加载提示透明 */
}

.search-box {
  margin-bottom: 15px;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.planet-id {
  font-size: 12px;
  color: #777;
}

/* 修改配置按钮样式 */
.config-btn {
  flex: 1;
  padding: 4px 8px; /* 减小内边距使按钮变小 */
  background: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px; /* 减小字体大小 */
  height: 26px; /* 固定高度使按钮更小 */
  margin-right: 10px; /* 按钮和复选框间距10px */
}

/* 修改配置切换区域样式 */
.config-toggle {
  display: flex;
  align-items: center;
  gap: 5px; /* 复选框和文字间距5px */
  font-size: 12px; /* 减小字体大小 */
}

/* 修改复选框样式 */
.config-toggle input[type="checkbox"] {
  margin: 0; /* 去除默认外边距 */
  width: 14px; /* 缩小复选框 */
  height: 14px; /* 缩小复选框 */
}

/* 修改选择列表样式 */
.selected-list {
  max-height: 200px;
  overflow-y: auto;
  padding: 6px; /* 减小内边距 */
}

.selected-item {
  padding: 4px 0; /* 减小内边距 */
  border-bottom: 1px solid #eee;
  font-size: 12px; /* 减小字体大小 */
}

/* 行星选项布局保持6列 */
.planet-options {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px; /* 增加间隙 */
  margin: 20px 0; /* 增加外边距 */
  max-height: 500px; /* 增加最大高度 */
  overflow-y: auto;
  padding: 8px; /* 增加内边距 */
}

/* 行星选项样式调整 */
.planet-option {
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  align-items: center;
  min-width: 90px; /* 增加最小宽度 */
  transition: all 0.2s; /* 添加悬停效果 */
  margin-bottom: 8px;
}

/* 行星悬停效果 */
.planet-option:hover {
  background: #e3f2fd;
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.planet-option label {
  display: flex;
  align-items: center;
  gap: 5px; /* 进一步缩小复选框与文字间距 */
  cursor: pointer;
  width: 100%;
}

.planet-name {
  flex: 1;
  font-weight: bold;
  margin-right: 8px;
  white-space: nowrap; /* 防止文字换行 */
  overflow: hidden; /* 隐藏溢出文本 */
  text-overflow: ellipsis; /* 溢出显示省略号 */
}

/* 添加滚动条样式 */
.planet-options::-webkit-scrollbar {
  width: 6px;
}

.planet-options::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

/* 减小复选框尺寸 */
.planet-option input[type="checkbox"] {
  margin: 0;
  width: 12px; /* 减小复选框宽度 */
  height: 12px; /* 减小复选框高度 */
}

/* 修改对话框布局为居中 */
.config-dialog {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center; /* 垂直居中 */
  justify-content: center; /* 水平居中 */
  z-index: 1000;
}

/* 对话框容器 */
.dialog-center {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

/* 对话框内容样式 */
/* 增大对话框宽度 */
.dialog-content {
  width: 800px; /* 增加宽度到800px */
  max-width: 90%; /* 确保在小屏幕上不会超出视口 */
  background: white;
  border-radius: 8px;
  padding: 20px; /* 增加内边距 */
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4); /* 增强阴影效果 */
}

/* 对话框按钮容器优化 - 精确右下角小尺寸 */
.dialog-buttons {
  display: flex;
  justify-content: flex-end; /* 确保按钮居右 */
  gap: 8px; /* 减小按钮间距 */
  margin-top: 10px; /* 减小与上方元素的间距 */
  padding-top: 10px; /* 添加顶部内边距 */
  width: auto; /* 关键：宽度自适应 */
  margin-left: auto; /* 关键：右侧对齐 */
}

/* 对话框按钮样式 - 紧凑小尺寸 */
.dialog-buttons button {
  padding: 6px 14px !important; /* 重要：覆盖其他样式 */
  font-size: 13px !important;
  border-radius: 4px !important;
  border: 1px solid #ddd !important;
  cursor: pointer;
  transition: all 0.2s;
  width: auto !important; /* 关键：宽度自适应内容 */
  min-width: 60px !important; /* 最小宽度保证一致性 */
  height: 32px !important; /* 固定高度避免拉伸 */
  white-space: nowrap !important; /* 防止文字换行 */
  box-sizing: border-box !important; /* 包含边框和内边距 */
}

/* 取消按钮样式 */
.btn-cancel {
  background: #f5f5f5 !important;
  color: #555 !important;
}

/* 确定按钮样式 */
.btn-confirm {
  background: #4285f4 !important;
  color: white !important;
  border-color: #4285f4 !important;
}

/* 按钮悬停效果 */
.dialog-buttons button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.btn-cancel:hover {
  background: #e8e8e8 !important;
}

.btn-confirm:hover {
  background: #3367d6 !important;
  border-color: #3367d6 !important;
}

</style>
